import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import {
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";

import { ChatRecord } from "@/shared/domain/chat";

import styles from "./ChatsTable.module.css";

interface ChatsTableProps {
  chats: ChatRecord[];
  isLoading: boolean;
  selectedChatId: string | undefined;
  onSelectChat: (chatId: string) => void;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  onNextPage: () => void;
  onPreviousPage: () => void;
  currentPage: number;
}

export function ChatsTable({
  chats,
  isLoading,
  selectedChatId,
  onSelectChat,
  hasNextPage,
  hasPreviousPage,
  onNextPage,
  onPreviousPage,
  currentPage,
}: ChatsTableProps) {
  if (isLoading && chats.length === 0) {
    return (
      <div className={styles.emptyState}>
        <CircularProgress size="1.5rem" />
      </div>
    );
  }

  if (chats.length === 0 && !hasPreviousPage) {
    return <div className={styles.emptyState}>No chats found.</div>;
  }

  return (
    <div>
      <TableContainer className={styles.tableContainer}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Session ID</TableCell>
              <TableCell>Created By</TableCell>
              <TableCell>Org ID</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Model</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {chats.map((chat) => {
              const isSelected = chat.session_id === selectedChatId;
              return (
                <TableRow
                  key={chat.session_id}
                  onClick={() => onSelectChat(chat.session_id)}
                  className={`${styles.clickableRow} ${isSelected ? styles.selectedRow : ""}`}
                >
                  <TableCell className={styles.cellTruncate}>
                    {chat.title || "Untitled"}
                  </TableCell>
                  <TableCell className={styles.cellTruncate}>
                    {chat.session_id}
                  </TableCell>
                  <TableCell className={styles.cellTruncate}>
                    {chat.created_by}
                  </TableCell>
                  <TableCell className={styles.cellTruncate}>
                    {chat.org_id}
                  </TableCell>
                  <TableCell>
                    {new Date(chat.created).toLocaleString()}
                  </TableCell>
                  <TableCell>{chat.model_profile || "-"}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <div className={styles.paginationContainer}>
        <span className={styles.pageIndicator}>Page {currentPage + 1}</span>
        <div className={styles.paginationButtons}>
          <IconButton
            size="small"
            onClick={onPreviousPage}
            disabled={!hasPreviousPage || isLoading}
            aria-label="Previous page"
          >
            <NavigateBeforeIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={onNextPage}
            disabled={!hasNextPage || isLoading}
            aria-label="Next page"
          >
            <NavigateNextIcon />
          </IconButton>
        </div>
      </div>
    </div>
  );
}
